<template>
  <validation-observer
    v-show="isRenderAfterFullfilled"
    v-if="shouldQuestionRender"
    v-slot="observer"
    slim
  >
    <form-section
      v-if="!isVehicleSelected"
      v-bind="observer"
      :validated="observer.validated"
    >
      <!-- OTHER VEHICLE DIALOG CONFIRMATION -->
      <v-dialog
        v-model="isOtherVehicleDialogShown"
        persistent
        max-width="500"
      >
        <v-card>
          <v-card-actions class="d-flex justify-end">
            <qz-btn
              icon
              data-test="other-vehicle-modal-close"
              @click="hideOtherVehicleDialog"
            >
              <v-icon>mdi-close</v-icon>
            </qz-btn>
          </v-card-actions>

          <v-card-title class="headline pt-0">
            <span class="wrap-words">Your vehicle is listed as a {{ otherVehicleType }}</span>
          </v-card-title>
          <v-card-text>
            <p>Our {{ productName }} insurance providers are unable to provide an insurance quote for your vehicle as it seems to be classed as a {{ otherVehicleType }}, rather than a {{ productName }}.</p>
            <p class="font-weight-bold">
              You can continue to our {{ otherVehicleType }} insurance comparison system
            </p>
            <p class="text-center">
              <qz-btn
                color="primary"
                x-large
                rounded
                tag="a"
                data-test="other-vehicle-modal-go-to"
                :href="otherVehicleDialogLink"
                target="_blank"
              >
                <span>Get Quote</span>
              </qz-btn>
            </p>
            <p>Otherwise, you can continue with a {{ productName }} insurance quote and change to another vehicle.</p>
            <p class="text-center">
              <qz-btn
                x-large
                rounded
                data-test="other-vehicle-modal-close"
                @click="hideOtherVehicleDialog"
              >
                <span>Change Vehicle</span>
              </qz-btn>
            </p>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- VEHICLE SELECTOR -->
      <vehicle-selector
        id="select-vehicle"
        :key="vehicleSelectorKey"
        ref="vehicleSelector"
        :title="title"
        :help-text="helpText"
        :vehicle-type="vehicleType"
        :step-name="stepId"
        :initial-reg-number="stepData.vehicleRegistrationNumber"
        :show-registration-number="showRegistrationNumber"
        :fields="fields"
        :vehicle-type-label="vehicleTypeLabel"
        :type-options="getTypeOptions"
        :form-observer="formObserver"
        reg-number-rules="regNumber"
        @toast="$emit('toast', $event)"
        @loading="$emit('loading', $event)"
        @select-vehicle="onVehicleSelect"
        @other-vehicle-dialog="onOtherVehicleDialog"
        @prefill-vehicle-bought="onPrefillVehicleBought"
        @prefill-estimated-value="onPrefillEstimatedValue"
      />

      <validation-provider
        v-slot="{ failed }"
        name="Vehicle"
        vid="vehicle_vehicle-confirm"
        rules="required"
      >
        <input
          v-model="stepData.vehicleConfirm"
          type="hidden"
        >
        <div
          v-if="failed"
          class="error--text text-body-1 mt-4"
        >
          Please select your {{ vehicleTypeLabel }} to proceed.
        </div>
      </validation-provider>
    </form-section>
    <form-section
      v-else
      valid
    >
      <form-row
        id="select-vehicle"
        :label="`Your ${vehicleTypeLabel}`"
        :is-vertical-splitted="false"
      >
        <vehicle-selector-preview>
          <icon
            slot="icon"
            size="56"
            :icon="vehicleIcon"
          />

          <p
            v-if="showRegistrationNumber && stepData.vehicleRegistrationNumber"
            class="wrap-words font-weight-bold"
          >
            {{ stepData.vehicleRegistrationNumber }}
          </p>
          <p>{{ stepData.vehicleMakeLabel || stepData.vehicleMake }}, {{ stepData.vehicleModelLabel || stepData.vehicleModel }}, {{ stepData.yearOfManufacture }}</p>
          <p>
            {{ [
              getFuelLabel(stepData.engineSize),
              ![undefined, null, ''].includes(stepData.doors) ? `${stepData.doors} Door` : null,
              ![undefined, null, ''].includes(stepData.transmission) ? constants.VEHICLE_TRANSMISSION_TYPES[stepData.transmission] : null,
              ![undefined, null, ''].includes(stepData.vehicleWeight) ? `Gross Weight ${stepData.vehicleWeight} kg` : null,
            ].filter(s => s !== null).join(', ') }}
          </p>
        </vehicle-selector-preview>
        <div class="mt-3 d-flex">
          <qz-btn
            color="primary"
            text
            class="text-decoration-underline mr-2"
            data-test="not_my_car"
            x-large
            @click.prevent="resetVehicle"
          >
            <span>This is not my {{ vehicleTypeLabel }}</span>
          </qz-btn>
        </div>
      </form-row>
      <validation-provider
        vid="vehicle_vehicle-confirm"
        rules="required"
      >
        <input
          :value="stepData.vehicleConfirm"
          type="hidden"
        >
      </validation-provider>
    </form-section>
  </validation-observer>
</template>
<script>
import { pick } from 'lodash';
import { ExtDate } from '@/helpers/date';
import QuestionBase from './Base';
import VehicleSelector from '@/components/VehicleSelector/VehicleSelector';
import VehicleSelectorPreview from '@/components/VehicleSelector/VehicleSelectorPreview';

export default {
  name: 'question-vehicle',
  extends: QuestionBase,
  components: {
    VehicleSelectorPreview,
    VehicleSelector,
  },
  props: {
    prefiledField: {
      type: String,
      default: 'estimatedValue',
    },
    typeOptions: {
      type: Object,
      default: () => {},
    },
  },
  data () {
    return {
      fields: Object.keys(this.constants.VEHICLE_FIELDS).filter(key => this.constants.VEHICLE_FIELDS[key] === true || this.constants.VEHICLE_FIELDS[key].includes(this.productName)),
      isOtherVehicleDialogShown: false,
      otherVehicleType: null,
      otherVehicleDialogLink: null,
      vehicleSelectorKey: 0,
    };
  },
  computed: {
    getTypeOptions () {
      if (!this.typeOptions) return [];

      return this.helpers.createOptions({ source: this.typeOptions });
    },
    vehicleIcon () {
      switch (this.vehicleType) {
        case 'van':
        case 'cateringvan':
          return 'mdi-van-utility';
        case 'bike':
        case 'scooter / motorcycle':
          return 'mdi-motorbike';
        case 'taxi':
          return 'mdi-taxi';
        case 'minibus':
          return 'mdi-bus-school';
        case 'motorhome':
          return 'mdi-rv-truck';
        case 'car':
        default:
          return 'mdi-car';
      }
    },
    vehicleType () {
      if (['drivingschool'].includes(this.productName)) {
        return 'car';
      }

      if (['breakdown', 'convicteddriver', 'impound', 'courier'].includes(this.productName) && this.stepData.vehicleType && Object.keys(this.constants.VEHICLE_TYPES).includes(this.stepData.vehicleType)) {
        return this.constants.VEHICLE_TYPES[this.stepData.vehicleType].toLowerCase();
      }

      return this.productName;
    },
    isVehicleSelected () { return this.stepData.vehicleConfirm; },
    vehicleTypeLabel () {
      if (['cateringvan', 'breakdown'].includes(this.vehicleType)) return 'vehicle';
      return this.vehicleType;
    },
    showRegistrationNumber () {
      return !this.stepData?.hideRegNumber;
    },
  },
  watch: {
    'stepData.vehicleType': async function () {
      await this.resetVehicle();
      this.getDataFromSession = this.sessionData.vehicleType === this.stepData.vehicleType;
    },
  },
  methods: {
    hideOtherVehicleDialog () {
      this.isOtherVehicleDialogShown = false;
      this.otherVehicleDialogLink = null;
      this.questionData.vehicleRegistrationNumber = null;
      this.vehicleSelectorKey += 1;
    },
    async onVehicleSelect (vehicle) {
      this.questionData = pick(vehicle, this.fields);
      this.resetFormObserver();

      await this.$nextTick();
      this.$scrollTo('#select-vehicle', 300);
    },
    onOtherVehicleDialog ({ vehicleType, link }) {
      this.otherVehicleDialogLink = link;
      this.otherVehicleType = vehicleType;
      this.isOtherVehicleDialogShown = true;
    },
    onPrefillVehicleBought (lastSaleDate) {
      this.$emit('input', { vehicleBought: lastSaleDate ? new ExtDate(lastSaleDate).format() : null });
    },
    onPrefillEstimatedValue (estimatedValue) {
      // no need to prefill estimated value if there's no such field yet at all
      if (!Object.prototype.hasOwnProperty.call(this.stepData, this.prefiledField)) return;

      this.$emit('set-step-shared-data', { estimatedValue });
    },
    getFuelLabel (engineSize) {
      const fuelId = engineSize[engineSize.length - 1];
      if (!Object.keys(this.constants.VEHICLE_FUEL_TYPES).includes(fuelId)) return engineSize;
      return `${engineSize.slice(0, -1)} cc ${this.constants.VEHICLE_FUEL_TYPES[fuelId]}`;
    },
    async resetVehicle () {
      this.$emit('reset-observer');
      Object.keys(this.questionData).forEach(key => {
        this.questionData[key] = null;
      });

      if (Object.prototype.hasOwnProperty.call(this.stepData, this.prefiledField)) {
        this.$emit('input', { [this.prefiledField]: null });
        this.$emit('set-step-shared-data', { estimatedValue: null });
      }

      if (Object.prototype.hasOwnProperty.call(this.stepData, 'vehicleBought')) {
        this.$emit('input', { vehicleBought: null });
      }

      // Required to properly reset dependant validation-providers state
      // on formObserver reset
      // TODO find better solution on refactor
      await this.$nextTick();
      await this.$nextTick();

      this.resetFormObserver();

      await this.$nextTick();

      this.$scrollTo('#select-vehicle', 300);
    },
    resetFormObserver () {
      if (this.productName !== 'breakdown') {
        this.formObserver.reset();
      }
    },
  },
};
</script>
